<script setup lang="ts">
/**
 * Page publique « TOUT ce que cette association propose sur une feature ».
 *
 * POURQUOI (Karim 2026-09-12) : « une asso a plusieurs billetteries, elle a
 * besoin d'une page qui liste tout, pour mettre le lien sur ses réseaux et que
 * les gens choisissent l'événement de leur choix. »
 *
 * La page de l'association montre bien les cinq domaines, mais QUATRE éléments
 * par section seulement, et le « voir plus » ouvre une modale : rien
 * d'adressable, donc rien de partageable.
 *
 * Un seul composant pour les cinq domaines (cagnottes, billetterie, adhésions,
 * boutiques, QR codes). Les pages qui l'utilisent ne portent que ce qui leur
 * est propre : le libellé, l'endpoint, la forme de la carte.
 */
import { formatDateAssociation, fuseauDe, instantAssociation } from "~/utils/heureAssociation";
// Lieu d'un événement en une ligne (§10) : nom, ville, territoire ou pays.
import { ligneLieuCarte } from "#shared/utils/lieuPublic";
import {
  libellesCategorieAsso,
  urlLogoAsso,
} from "~/utils/associationEntete";
import { associationListesApi } from "~/services/api/organizations";
import {
  useAssoListeDomaine,
  type DomaineAsso,
} from "~/data/queries/asso-liste.queries";

// Montants et dates dans la langue de la page.
const { formatCurrency } = useFormatLangue();

const { t } = useI18n();

// Les cinq domaines n'ont pas les mêmes champs ; l'accès dynamique est assumé.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Element = Record<string, any>;

const props = defineProps<{
  /** Slug de l'association, tel qu'il est dans l'URL. */
  slug: string;
  domaine: DomaineAsso;
  /** « Les événements de X » — le titre visible ET la balise title. */
  titre: string;
  /** Une phrase sous le titre, qui dit ce qu'on peut y faire. */
  sousTitre: string;
  /** Ce qu'on affiche quand l'association n'a rien publié ici. */
  messageVide: string;
}>();

const config = useRuntimeConfig();
const apiUrl = config.public.apiUrl;
const assetsUrl = `${apiUrl}/api/assets`;

const TAILLE_PAGE = 24;

/**
 * En-tête de l'association (nom, logo, bannière) — une seule lecture, hors
 * pagination.
 */
const { data: assoData } = await useAsyncData(
  `asso-entete-${props.slug}`,
  () => associationListesApi.entete(apiUrl, props.slug),
);

// Association introuvable → 404 franc. Un écran vide laisserait croire à une
// page valide, et le robot l'indexerait.
if (!assoData.value) {
  throw createError({ statusCode: 404, statusMessage: t("association.listeFeature.introuvable") });
}

/**
 * Pagination par `useInfiniteQuery` (pourquoi : cf. `useAssoListeDomaine`).
 *
 * SEO : `suspense()` côté serveur résout la PREMIÈRE page, donc les cartes
 * sont dans le HTML rendu — sans ça un robot ne verrait qu'un cadre vide.
 */
const listeQuery = useAssoListeDomaine({
  slug: () => props.slug,
  domaine: () => props.domaine,
  taillePage: TAILLE_PAGE,
});

// SSR : la première page doit être dans le HTML servi (cartes indexables).
// `allSettled` — une liste en erreur ne doit pas emporter l'en-tête.
if (import.meta.server) {
  await Promise.allSettled([listeQuery.suspense()]);
}

const elements = computed<Element[]>(() =>
  (listeQuery.data.value?.pages ?? []).flatMap((p) => p.data),
);
const total = computed(
  () => listeQuery.data.value?.pages?.[0]?.pagination.total ?? 0,
);
const encore = computed(() => Boolean(listeQuery.hasNextPage.value));
const chargementSuite = computed(() =>
  Boolean(listeQuery.isFetchingNextPage.value),
);

function chargerSuite() {
  if (!listeQuery.hasNextPage.value || listeQuery.isFetchingNextPage.value) {
    return;
  }
  listeQuery.fetchNextPage();
}

const nomAsso = computed(() => String(assoData.value?.name ?? ""));

/**
 * MÊME en-tête que la page de l'association : bannière, logo, nom, catégorie,
 * bouton « Soutenir ». Karim 2026-09-12 : « exactement pareil ». On monte donc
 * `AssociationsPageHeader` tel quel plutôt que d'en refaire une version
 * approchante, qui aurait dérivé au premier changement de design.
 */
const entete = computed(() => assoData.value ?? null);
const urlLogo = urlLogoAsso(assetsUrl);

/**
 * Champs de la bannière, convertis ICI et non dans le template : un `as string
 * | null` en attribut fait lire le `|` comme un filtre Vue 2 par le linter.
 */
const texteOuNull = (v: unknown): string | null =>
  typeof v === "string" && v ? v : null;
const logoAsso = computed(() => texteOuNull(entete.value?.logo));
const couvertureAsso = computed(() => texteOuNull(entete.value?.cover_image));
const banniereSecours = computed(() =>
  texteOuNull(entete.value?.fallback_banner),
);

/**
 * Chaque domaine nomme son image autrement : `cover_image` pour un événement
 * ou une boutique, `image` pour une cagnotte, `file_id` pour un QR code.
 * Sans ce choix, les cartes cagnotte tombaient toutes sur l'icône de repli.
 */
function fichierImage(e: Element): string | null {
  return (e.cover_image ?? e.image ?? e.file_id ?? null) as string | null;
}

function image(fileId: string | null, w = 600, h = 400): string | null {
  return fileId
    ? `${assetsUrl}/${fileId}?width=${w}&height=${h}&fit=cover&format=webp&quality=80`
    : null;
}

/**
 * Jour de l'événement dans le fuseau de l'ASSOCIATION (`timezone`, Paris par
 * défaut) ; chaîne naïve de la base = UTC (lot 2, 2026-09-25).
 */
function dateLisible(valeur: string | null, fuseau?: string | null): string | null {
  return (
    formatDateAssociation(valeur, fuseau, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }) || null
  );
}

/**
 * Palette des repères, par SENS et non par domaine — pour qu'un même vert
 * veuille dire la même chose sur une billetterie et sur une cagnotte.
 *
 *   vert   → on peut y aller (places libres, objectif atteint)
 *   ambre  → il faut se dépêcher (dernières places)
 *   rouge  → c'est fermé (complet)
 *   ardoise→ c'est derrière nous (terminé)
 *   bleu   → simple information chiffrée (participants, donateurs, articles)
 */
/**
 * ⚠️ DEUX RÈGLES, posées par Karim le 2026-09-12 :
 *
 *  1. **Jamais de fond transparent.** Un badge posé sur une carte blanche puis
 *     sur une image n'aurait pas la même densité, et le texte s'y délaverait.
 *  2. **Jamais le fond et le texte dans la même couleur.** Un bleu sur bleu
 *     clair « tient » à l'œil du concepteur et disparaît partout ailleurs.
 *
 * D'où : fond PLEIN et saturé, texte blanc. Les teintes reprennent celles déjà
 * employées ailleurs sur le site (`bg-amber-500`, `bg-red-600`,
 * `bg-emerald-500`, `bg-gray-600`) plutôt que d'en inventer une sixième série.
 */
const BADGE = {
  vert: "bg-emerald-500 text-white",
  ambre: "bg-amber-500 text-white",
  rouge: "bg-red-600 text-white",
  ardoise: "bg-gray-600 text-white",
  bleu: "bg-[#0c3097] text-white",
} as const;

/**
 * « Dernières places » : au plus cinq, ou moins d'un dixième de la salle.
 * Les deux critères, parce que 5 places sur 20 n'est pas la même urgence que
 * 5 places sur 2000 — et que 50 places sur 2000 non plus.
 */
function presqueComplet(e: Element): boolean {
  const restant = Number(e.places_restantes ?? 0);
  const capacite = Number(e.max_capacity ?? 0);
  if (restant <= 0 || capacite <= 0) return false;
  return restant <= 5 || restant / capacite <= 0.1;
}

/** Un événement déjà passé ne se réserve plus : on le dit au lieu de l'ignorer. */
function estPasse(e: Element): boolean {
  if (!e.start_date) return false;
  const d = instantAssociation(e.start_date);
  return !!d && d.getTime() < Date.now();
}

/**
 * Résumé affichable d'un élément : sa description débarrassée du HTML.
 *
 * Les descriptions viennent d'un éditeur riche — les balises brutes n'ont rien
 * à faire dans une carte, et un `v-html` y ouvrirait une injection.
 */
function resume(e: Element): string {
  return sansHtml((e.description ?? null) as string | null);
}

function sansHtml(html: string | null): string {
  return (html ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * ⚠️ `<component :is="'NuxtLink'">` — la chaîne — NE RÉSOUT PAS un composant
 * auto-importé : Vue rend alors une balise `<NuxtLink>` inconnue du navigateur,
 * sans lien cliquable. Constaté au rendu réel de la page (2026-09-12). Il faut
 * la RÉFÉRENCE du composant.
 */
const LienNuxt = resolveComponent("NuxtLink");

/** Adresse publique d'un élément, selon son domaine. */
function lien(e: Element): string {
  switch (props.domaine) {
    case "campaigns":
      return `/c/${e.slug}`;
    case "events":
      return `/b/${e.slug}`;
    case "membership-plans":
      return `/a/${e.slug}`;
    case "shops":
      return `/boutique/${e.slug}`;
    case "qr-codes":
      // Un QR code n'a pas de page à lui : sa carte n'est pas cliquable.
      return "";
  }
}

// ── Référencement ───────────────────────────────────────────────────────────
//
// Indexée SEULEMENT si elle a quelque chose à montrer. Une page « toutes les
// boutiques » vide n'apporte rien à personne et dilue le site — la règle des
// hubs ville (2026-08-29).
const siteUrl = useShareBaseUrl();
const url = `${siteUrl}/associations/${props.slug}/${
  { campaigns: "cagnottes", events: "billetterie", "membership-plans": "adhesions", shops: "boutiques", "qr-codes": "qr-codes" }[props.domaine]
}`;

const robotsContent = useRobotsContent(() => total.value === 0);
useSeoMeta({
  title: () => `${props.titre} — ${nomAsso.value} | Donoor`,
  description: () => props.sousTitre,
  ogTitle: () => `${props.titre} — ${nomAsso.value}`,
  ogDescription: () => props.sousTitre,
  ogUrl: url,
  ogType: "website",
  // useRobotsContent : staging jamais indexé, version traduite en noindex
  // (annuaire local indexé en français seulement — audit SEO 2026-10-18).
  robots: () => robotsContent.value,
});
useHead({ link: [{ rel: "canonical", href: url }] });
</script>

<template>
  <div class="min-h-screen flex flex-col bg-[#fafafa]">
    <div class="grow">
      <!-- MÊME haut de page que la billetterie et que la page de
           l'association : bannière FIXE, puis un panneau arrondi qui remonte
           par-dessus et glisse au défilement. Karim 2026-09-12. -->
      <AssociationsPageBanniereFixe
        v-if="entete"
        :name="nomAsso"
        :logo="logoAsso"
        :cover-image="couvertureAsso"
        :fallback-banner="banniereSecours"
        :assets-url="assetsUrl"
      />

      <div class="relative z-10 -mt-[68px] rounded-t-[28px] bg-[#fafafa] pt-8">
        <AssociationsPageHeader
          v-if="entete"
          :org="entete as never"
          :assets-url="assetsUrl"
          :category-label="libellesCategorieAsso()"
          :get-logo-url="urlLogo"
          balise-nom="p"
          :afficher-cta="false"
        />

        <div class="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-16 sm:pb-20">
        <!-- Retour à l'association : sans lui, la page partagée sur les réseaux
             est un cul-de-sac. -->
        <NuxtLink
          :to="`/associations/${slug}`"
          class="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6"
        >
          <UIcon name="i-lucide-arrow-left" class="size-4" />
          {{ $t("association.listeFeature.retourPage") }}
        </NuxtLink>

        <header class="mb-8">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-gray-900">
            {{ titre }}
          </h1>
          <p class="mt-2 text-gray-600">{{ sousTitre }}</p>
          <p v-if="total > 0" class="mt-1 text-sm text-gray-400">
            {{ $t("association.listeFeature.auTotal", { n: total }) }}
          </p>
        </header>

        <!-- Rien à montrer -->
        <div
          v-if="elements.length === 0"
          class="bg-white rounded-3xl border border-gray-200 p-10 text-center"
        >
          <UIcon name="i-lucide-inbox" class="size-10 text-gray-300 mb-3" />
          <p class="text-gray-500">{{ messageVide }}</p>
          <NuxtLink
            :to="`/associations/${slug}`"
            class="inline-block mt-4 text-sm font-semibold text-[#0c3097] hover:underline"
          >
            {{ $t("association.listeFeature.voirPage") }}
          </NuxtLink>
        </div>

        <!-- La liste -->
        <ul v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <li v-for="e in elements" :key="e.id">
            <component
              :is="lien(e) ? LienNuxt : 'div'"
              :to="lien(e) || undefined"
              class="group flex h-full flex-col bg-white rounded-3xl border border-gray-200 overflow-hidden transition-shadow"
              :class="lien(e) ? 'hover:shadow-[0_8px_28px_rgba(0,0,0,0.08)]' : ''"
            >
              <!-- HAUTEUR FIXE, pas un ratio : un ratio grandit avec la carte, donc
                   l'image redevenait imposante sur grand écran. 144 px suffisent
                   à reconnaître une affiche ; le titre, la description et les
                   repères tiennent alors tous au-dessus du pli.
                   Karim 2026-09-12, après deux réductions par ratio jugées
                   insuffisantes (3/2 puis 16/9). -->
              <div class="h-36 bg-gray-100 overflow-hidden">
                <img
                  v-if="image(fichierImage(e))"
                  :src="image(fichierImage(e))!"
                  :alt="e.title"
                  loading="lazy"
                  class="w-full h-full object-cover transition-transform duration-300"
                  :class="lien(e) ? 'group-hover:scale-[1.03]' : ''"
                >
                <div v-else class="w-full h-full flex items-center justify-center">
                  <UIcon name="i-lucide-image" class="size-8 text-gray-300" />
                </div>
              </div>

              <!-- `grow` sur le corps + `mt-auto` sur la rangée de repères :
                   les badges tombent au MÊME niveau d'une carte à l'autre, que
                   le titre tienne sur une ligne ou deux et qu'il y ait un lieu
                   ou non. Karim 2026-09-12. -->
              <div class="p-4 flex grow flex-col">
                <!-- UNE SEULE LIGNE, coupée par « … » si le titre déborde (`truncate`).
                     Sur deux lignes, les cartes d'une même rangée ne
                     commençaient pas leur date au même niveau.
                     ⚠️ `truncate` et non `line-clamp-1` : il pose
                     `white-space: nowrap`, donc la coupe tient même si le
                     conteneur change de `display`. -->
                <h2 class="truncate text-lg font-bold text-gray-900">
                  {{ e.title }}
                </h2>

                <!-- Événement : quand et où -->
                <p
                  v-if="domaine === 'events' && dateLisible(e.start_date, fuseauDe(e))"
                  class="mt-1.5 text-sm text-gray-600 first-letter:uppercase"
                >
                  {{ dateLisible(e.start_date, fuseauDe(e)) }}
                </p>
                <!-- Lieu : règle partagée `formaterLieu` (« Salle des fêtes,
                     Saint-Denis, La Réunion ») — le nom seul ne disait pas
                     où, la ville seule confondait les homonymes. -->
                <p
                  v-if="domaine === 'events' && ligneLieuCarte(e)"
                  class="text-sm text-gray-400 line-clamp-1"
                >
                  {{ ligneLieuCarte(e) }}
                </p>

                <!-- Description — deux lignes maximum, `line-clamp` pose les
                     « … » de lui-même.
                     ⚠️ PAS de classe `block` ici : en Tailwind v4 elle et
                     `line-clamp-2` posent toutes deux `display`, et la
                     troncature disparaît sans prévenir. -->
                <p
                  v-if="domaine !== 'shops' && resume(e)"
                  class="mt-1.5 text-sm text-gray-500 line-clamp-2"
                >
                  {{ resume(e) }}
                </p>

                <!-- Repères — toujours en bas de carte (`mt-auto`). -->
                <div
                  v-if="domaine === 'events'"
                  class="mt-auto pt-3 flex flex-wrap items-center gap-1.5"
                >
                  <span
                    v-if="estPasse(e)"
                    class="inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-semibold"
                    :class="BADGE.ardoise"
                  >
                    {{ $t("association.listeFeature.termine") }}
                  </span>
                  <span
                    v-else-if="e.places_restantes === 0"
                    class="inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-semibold"
                    :class="BADGE.rouge"
                  >
                    {{ $t("association.listeFeature.complet") }}
                  </span>
                  <span
                    v-else-if="e.places_restantes != null"
                    class="inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-semibold"
                    :class="presqueComplet(e) ? BADGE.ambre : BADGE.vert"
                  >
                    {{
                      presqueComplet(e)
                        ? $t("association.listeFeature.plusQuePlaces", { n: e.places_restantes }, e.places_restantes)
                        : $t("association.listeFeature.placesRestantes", { n: e.places_restantes }, e.places_restantes)
                    }}
                  </span>
                  <span
                    v-if="(e.tickets_sold ?? 0) > 0"
                    class="inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-medium"
                    :class="BADGE.bleu"
                  >
                    {{ $t("association.listeFeature.participants", { n: e.tickets_sold }, e.tickets_sold) }}
                  </span>
                </div>

                <!-- Cagnotte : où en est la collecte -->
                <template v-if="domaine === 'campaigns'">
                  <div class="mt-3 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      class="h-full rounded-full bg-[#0c3097]"
                      :style="{ width: `${Math.min(100, Number(e.percent ?? 0))}%` }"
                    />
                  </div>
                  <p class="mt-2 text-sm text-gray-600">
                    <i18n-t :keypath="Number(e.goal_amount ?? 0) > 0 ? 'association.listeFeature.collectesSur' : 'association.listeFeature.collectes'">
                      <template #montant>
                        <span class="font-semibold text-gray-900">{{ formatCurrency(Number(e.raised ?? 0)) }}</span>
                      </template>
                      <template #objectif>{{ formatCurrency(Number(e.goal_amount ?? 0)) }}</template>
                    </i18n-t>
                  </p>
                  <div class="mt-auto pt-3 flex flex-wrap items-center gap-1.5">
                    <span
                      v-if="Number(e.percent ?? 0) >= 100"
                      class="inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-semibold"
                      :class="BADGE.vert"
                    >
                      {{ $t("association.listeFeature.objectifAtteint") }}
                    </span>
                    <span
                      v-if="(e.donors_count ?? 0) > 0"
                      class="inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-medium"
                      :class="BADGE.bleu"
                    >
                      {{ $t("association.listeFeature.donateurs", { n: e.donors_count }, e.donors_count) }}
                    </span>
                  </div>
                </template>

                <!-- Adhésion : le prix -->
                <p
                  v-else-if="domaine === 'membership-plans' && e.price != null"
                  class="mt-1.5 text-sm font-semibold text-gray-900"
                >
                  <!-- « à partir de Gratuit » ne veut rien dire : quand la
                       formule la moins chère est à 0, on dit simplement
                       « Gratuit ». -->
                  <template
                    v-if="Number(e.price_count ?? 1) > 1 && Number(e.min_price ?? e.price) > 0"
                  >{{ $t("association.listeFeature.aPartirDe") }} </template>
                  {{
                    Number(e.min_price ?? e.price) > 0
                      ? formatCurrency(Number(e.min_price ?? e.price))
                      : $t("association.rightColumn.gratuit")
                  }}
                </p>

                <div
                  v-if="domaine === 'membership-plans'"
                  class="mt-auto pt-3 flex flex-wrap items-center gap-1.5"
                >
                  <span
                    v-if="Number(e.price_count ?? 1) > 1"
                    class="inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-medium"
                    :class="BADGE.bleu"
                  >
                    {{ $t("association.listeFeature.formules", { n: Number(e.price_count) }, Number(e.price_count)) }}
                  </span>
                </div>

                <!-- Boutique : sa description -->
                <template v-else-if="domaine === 'shops'">
                  <p
                    v-if="e.description"
                    class="mt-1.5 text-sm text-gray-500 line-clamp-2"
                  >
                    {{ sansHtml(e.description) }}
                  </p>
                  <div class="mt-auto pt-3 flex flex-wrap items-center gap-1.5">
                    <span
                      class="inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-medium"
                      :class="(e.products_count ?? 0) > 0 ? BADGE.bleu : BADGE.ardoise"
                    >
                      {{
                        (e.products_count ?? 0) > 0
                          ? $t("association.listeFeature.articlesEnVente", { n: e.products_count }, e.products_count)
                          : $t("association.listeFeature.aucunArticle")
                      }}
                    </span>
                  </div>
                </template>
              </div>
            </component>
          </li>
        </ul>

        <div v-if="encore" class="mt-8 text-center">
          <button
            type="button"
            :disabled="chargementSuite"
            class="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0c3097] text-white text-sm font-semibold hover:bg-[#0a2679] disabled:opacity-60 transition-colors"
            @click="chargerSuite"
          >
            <UIcon
              v-if="chargementSuite"
              name="i-lucide-loader-2"
              class="size-4 animate-spin"
            />
            {{ chargementSuite ? "Chargement…" : $t("commun.actions.voirPlus") }}
          </button>
        </div>
      </div>
      <!-- fin du contenu borné -->
      </div>
      <!-- fin du panneau arrondi -->
    </div>
    <!-- fin de la zone qui pousse le pied de page -->

    <!-- MÊME bouton flottant que la page de l'association (Karim 2026-09-12) :
         en bas à droite, il suit le défilement au lieu de disparaître avec
         l'en-tête dès qu'on descend dans la liste. -->
    <SupportDonoor
      v-if="entete?.payment_slug"
      :name="String(entete.name)"
      :url="`/pay/${entete.payment_slug}`"
    />
  </div>
</template>
